import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usersApi } from '../../api/users.api';
import { useAuth } from '../../context/AuthContext';
import { getBizuImageUrl } from '../../utils/images';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  StarIcon,
  CheckCircleIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Step mappings for each Bizu game
const stepMappings = {
  babo: {
    1: 'Park',
    2: 'Santa Ana',
    3: 'Bizumia'
  },
  beez: {
    1: 'Intro',
    2: 'Level 1 - Small',
    3: 'Level 1 - Small',
    4: 'Level 1 - Small',
    5: 'Level 2 - Big',
    6: 'Level 2 - Big',
    7: 'Level 3 - Both',
    8: 'Level 3 - Both',
    9: 'End'
  },
  queso: {
    1: 'Intro',
    2: 'Pick Product',
    3: 'Look',
    4: 'Sniff',
    5: 'Feel',
    6: 'Touch',
    7: 'Library',
    8: 'End'
  },
  kien: {
    1: 'Intro',
    2: 'Level 1 - Tutorial',
    3: 'Level 1 - Tutorial',
    4: 'Level 2 - BBQ',
    5: 'Level 2 - BBQ',
    6: 'Level 3 - Party',
    7: 'Level 3 - Party',
    8: 'New Recipe?',
    9: 'New Recipe',
    10: 'Ingredients',
    11: 'How to Cook',
    12: 'Recipe Display',
    13: 'End'
  },
  des: {
    1: 'Bus Intro',
    2: 'Cave Intro',
    3: 'Level 1 - 1 Bead',
    4: 'Level 1 - 1 Bead',
    5: 'Level 1 - 1 Bead',
    6: 'Level 1 - 1 Bead',
    7: 'Level 2 - 5 Beads',
    8: 'Level 2 - 5 Beads',
    9: 'Level 2 - 5 Beads',
    10: 'Level 2 - 5 Beads',
    11: 'Level 2 - 5 Beads',
    12: 'Level 3 - Both Beads',
    13: 'Level 3 - Both Beads',
    14: 'Level 3 - Both Beads',
    15: 'Level 3 - Both Beads',
    16: 'Level 3 - Both Beads',
    17: 'End'
  }
};

const getStepLabel = (bizuName, step) => {
  if (!step || step === 0) return '-';
  const mapping = stepMappings[bizuName?.toLowerCase()];
  if (!mapping) return `Step ${step}`;
  return mapping[step] || `Step ${step}`;
};

// End step for each Bizu game (matches backend logic)
const endSteps = {
  babo: 3,
  beez: 9,
  queso: 8,
  kien: 13,
  des: 17
};

const isCurrentlyInProgress = (bizuName, step, completed) => {
  const endStep = endSteps[bizuName?.toLowerCase()];
  if (!endStep) return !completed; // Default for unknown bizus (like babo)
  return (step || 0) < endStep;
};

export default function UserDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBizu, setSelectedBizu] = useState(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await usersApi.getDetails(id);
        setData(response.data);
      } catch (err) {
        setError('Failed to load user details');
        console.error('Error fetching user details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  const renderStars = (count) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3].map((star) => (
          star <= count ? (
            <StarIconSolid key={star} className="h-5 w-5 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="h-5 w-5 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-500">{error || 'User not found'}</p>
          <Button variant="secondary" onClick={() => navigate('/users')} className="mt-4">
            Back to Users
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { user, bizuProgress, userStats } = data;

  // Helper to format time
  const formatActiveTime = (ms) => {
    if (!ms) return '0m';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate(`/users/${id}/edit`)}>
            <PencilIcon className="h-5 w-5 mr-2" />
            Edit User
          </Button>
        )}
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start gap-6">
          <div className="p-4 bg-primary-100 rounded-full">
            <UserIcon className="h-12 w-12 text-primary-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {user.emails?.[0]?.address || user.username || 'Unknown'}
              </h2>
              {user.profile?.role === 'guest' && (
                <Badge variant="warning">Guest</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900">{user.emails?.[0]?.address || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Language</p>
                  <p className="text-gray-900">{user.profile?.language?.toUpperCase() || 'EN'}</p>
                </div>
              </div>
              {user.profile?.city && (
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">City</p>
                    <p className="text-gray-900">{user.profile.city}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                {user.profile?.music_off ? (
                  <SpeakerXMarkIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <SpeakerWaveIcon className="h-5 w-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm text-gray-500">Music</p>
                  <p className="text-gray-900">{user.profile?.music_off ? 'Off' : 'On'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-gray-900">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Stats Section */}
      {userStats && (userStats.totalLogins > 0 || userStats.totalSessions > 0) && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">User Stats</h2>
            {userStats.lastActiveDate && (
              <span className="text-sm text-gray-500">
                Last active: {new Date(userStats.lastActiveDate).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-4xl font-bold text-gray-900">
                {userStats.totalLogins || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Logins</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-4xl font-bold text-primary-600">
                {userStats.totalSessions || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Sessions</p>
            </div>
            <div
              className={`text-center p-6 bg-gray-50 rounded-lg ${userStats.totalPageVisits > 0 ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors`}
              onClick={userStats.totalPageVisits > 0 ? () => navigate(`/users/${id}/navigation-history`) : undefined}
            >
              <p className={`text-4xl font-bold ${userStats.totalPageVisits > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                {userStats.totalPageVisits || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Page Visits{userStats.totalPageVisits > 0 ? ' →' : ''}
              </p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-4xl font-bold text-green-600">
                {formatActiveTime(userStats.totalActiveTimeMs)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Active Time</p>
            </div>
          </div>
        </div>
      )}

      {/* Bizu Progress Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Bizu Progress ({bizuProgress.length})
          </h2>
        </div>

        {bizuProgress.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            This user hasn't started playing with any Bizu yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {bizuProgress.map((progress) => {
              const currentlyPlaying = isCurrentlyInProgress(progress.bizu, progress.step, progress.completed);
              return (
              <div
                key={progress.bizu}
                className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${currentlyPlaying ? 'border-primary-300 bg-primary-50/30' : ''}`}
                onClick={() => setSelectedBizu(progress)}
              >
                {currentlyPlaying && (
                  <div className="flex items-center gap-2 mb-3 text-primary-600">
                    <PlayIcon className="h-4 w-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">Currently Playing</span>
                  </div>
                )}
                <div className="flex items-center gap-4 mb-3">
                  <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={getBizuImageUrl(progress.bizu)}
                      alt={progress.displayName}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{progress.displayName}</h3>
                      <div
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: progress.color }}
                      />
                    </div>
                    {renderStars(progress.completed ? 3 : (progress.bizu === 'babo' ? progress.step : progress.stars))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Status</span>
                    <Badge variant={progress.completed ? 'success' : 'warning'}>
                      {progress.completed ? 'Completed' : 'In Progress'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Current Stage</span>
                    <span className="text-sm font-medium text-gray-900">
                      {getStepLabel(progress.bizu, progress.step)}
                    </span>
                  </div>
                  {progress.bizu === 'queso' && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Products</span>
                      <span className="text-sm font-medium text-gray-900">
                        {progress.productsCount} cards
                      </span>
                    </div>
                  )}
                  {progress.bizu === 'kien' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Recipes Played</span>
                        <span className="text-sm font-medium text-gray-900">
                          {progress.recipesPlayedCount}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Recipes Created</span>
                        <span className="text-sm font-medium text-gray-900">
                          {progress.recipesCreatedCount}
                        </span>
                      </div>
                    </>
                  )}
                  {progress.inMap && (
                    <div className="flex items-center gap-1 text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4" />
                      In Map
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bizu Progress Modal */}
      <Modal
        isOpen={!!selectedBizu}
        onClose={() => setSelectedBizu(null)}
        title={selectedBizu?.displayName ? `${selectedBizu.displayName} Progress` : 'Bizu Progress'}
        size="md"
      >
        {selectedBizu && (
          <div>
            {/* Bizu Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="h-20 w-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <img
                  src={getBizuImageUrl(selectedBizu.bizu)}
                  alt={selectedBizu.displayName}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-gray-900">{selectedBizu.displayName}</h3>
                  <div
                    className="w-5 h-5 rounded-full border"
                    style={{ backgroundColor: selectedBizu.color }}
                  />
                </div>
                {renderStars(selectedBizu.completed ? 3 : (selectedBizu.bizu === 'babo' ? selectedBizu.step : selectedBizu.stars))}
                <div className="mt-2">
                  <Badge variant={selectedBizu.completed ? 'success' : 'warning'}>
                    {selectedBizu.completed ? 'Completed' : 'In Progress'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Progress Details */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Game Progress</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Current Stage</span>
                    <span className="text-sm font-medium text-gray-900">
                      {getStepLabel(selectedBizu.bizu, selectedBizu.step)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Step Number</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedBizu.step} / {endSteps[selectedBizu.bizu?.toLowerCase()] || '?'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Stars Earned</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedBizu.completed ? 3 : (selectedBizu.bizu === 'babo' ? selectedBizu.step : selectedBizu.stars)} / 3
                    </span>
                  </div>
                </div>
              </div>

              {/* Bizu-specific data */}
              {selectedBizu.bizu === 'queso' && (
                <div
                  className={`bg-gray-50 rounded-lg p-4 ${selectedBizu.productsCount > 0 ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors`}
                  onClick={selectedBizu.productsCount > 0 ? () => navigate(`/bizu/queso/library/${id}`) : undefined}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700">Queso Library</h4>
                      <span className="text-sm text-gray-500">Products Collected</span>
                    </div>
                    <span className={`text-sm font-medium ${selectedBizu.productsCount > 0 ? 'text-primary-600' : 'text-gray-900'}`}>
                      {selectedBizu.productsCount} cards{selectedBizu.productsCount > 0 ? ' →' : ''}
                    </span>
                  </div>
                </div>
              )}

              {selectedBizu.bizu === 'kien' && (
                <div className="space-y-3">
                  <div
                    className={`bg-gray-50 rounded-lg p-4 ${selectedBizu.recipesPlayedCount > 0 ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors`}
                    onClick={selectedBizu.recipesPlayedCount > 0 ? () => navigate(`/bizu/kien/played-recipes/${id}`) : undefined}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700">Recipes Played</h4>
                        <span className="text-sm text-gray-500">Game recipes completed</span>
                      </div>
                      <span className={`text-sm font-medium ${selectedBizu.recipesPlayedCount > 0 ? 'text-primary-600' : 'text-gray-900'}`}>
                        {selectedBizu.recipesPlayedCount} recipes{selectedBizu.recipesPlayedCount > 0 ? ' →' : ''}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`bg-gray-50 rounded-lg p-4 ${selectedBizu.recipesCreatedCount > 0 ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors`}
                    onClick={selectedBizu.recipesCreatedCount > 0 ? () => navigate(`/bizu/kien/user-recipes/${id}`) : undefined}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700">Recipes Created</h4>
                        <span className="text-sm text-gray-500">User-created recipes</span>
                      </div>
                      <span className={`text-sm font-medium ${selectedBizu.recipesCreatedCount > 0 ? 'text-primary-600' : 'text-gray-900'}`}>
                        {selectedBizu.recipesCreatedCount} recipes{selectedBizu.recipesCreatedCount > 0 ? ' →' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              {selectedBizu.catchDate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Info</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-500">Caught on:</span>
                    <span className="font-medium text-gray-900">
                      {new Date(selectedBizu.catchDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="mt-6 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => navigate(`/bizu/${selectedBizu.bizu}`)}
              >
                View All {selectedBizu.displayName} Players
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
